import { useState } from "react";
import { Form, Input, InputNumber, Select } from "antd";
import { useCountries, useStates, useCities } from "../../../hooks/api/location/hooks";
import { TYPE_OPTS } from "./ClientsPage";

type ClientFormProps = {
  form: any;
  isEdit?: boolean;
  selectedCountryId: number | undefined;
  setSelectedCountryId: (id: number | undefined) => void;
  selectedStateId: number | undefined;
  setSelectedStateId: (id: number | undefined) => void;
};

const ClientForm = ({
  form,
  isEdit,
  selectedCountryId,
  setSelectedCountryId,
  selectedStateId,
  setSelectedStateId,
}: ClientFormProps) => {
  const [countrySearch, setCountrySearch] = useState("");
  const [stateSearch, setStateSearch] = useState("");
  const [citySearch, setCitySearch] = useState("");

  const { data: countriesRes, isFetching: loadingCountries } = useCountries(
    { search: countrySearch, limit: 20 },
  );
  const { data: statesRes, isFetching: loadingStates } = useStates(
    selectedCountryId,
    { search: stateSearch, limit: 20 },
  );
  const { data: citiesRes, isFetching: loadingCities } = useCities(
    selectedStateId,
    { search: citySearch, limit: 20 },
  );

  const countries = countriesRes?.data ?? [];
  const states = statesRes?.data ?? [];
  const cities = citiesRes?.data ?? [];

  return (
    <>
      <div className="grid grid-cols-2 gap-x-4">
        <Form.Item
          name="client_type"
          label="Client Type"
          rules={[{ required: true, message: "Please select client type" }]}
        >
          <Select
            placeholder="Select type"
            options={TYPE_OPTS.filter((o) => o.value)}
          />
        </Form.Item>

        <Form.Item name="account_reference" label="Account Ref">
          <Input placeholder="CLI-010" />
        </Form.Item>
      </div>

      <Form.Item
        name="business_name"
        label="Business Name"
        dependencies={["first_name"]}
        rules={[
          {
            validator: async (_rule, value) => {
              const firstName = form.getFieldValue("first_name");
              if (!String(value ?? "").trim() && !String(firstName ?? "").trim()) {
                throw new Error("Enter a business name or a first name.");
              }
            },
          },
        ]}
      >
        <Input placeholder="Company name…" />
      </Form.Item>

      <div className="grid grid-cols-2 gap-x-4">
        <Form.Item
          name="first_name"
          label="First Name"
          dependencies={["business_name"]}
          rules={[
            {
              validator: async (_rule, value) => {
                const businessName = form.getFieldValue("business_name");
                if (!String(value ?? "").trim() && !String(businessName ?? "").trim()) {
                  throw new Error("Enter a first name or a business name.");
                }
              },
            },
          ]}
        >
          <Input placeholder="First name" />
        </Form.Item>

        <Form.Item name="last_name" label="Last Name">
          <Input placeholder="Last name" />
        </Form.Item>
      </div>

      <div className="grid grid-cols-2 gap-x-4">
        <Form.Item
          name="email"
          label="Email"
          rules={[{ type: "email", message: "Invalid email address" }]}
        >
          <Input placeholder="billing@company.com" />
        </Form.Item>

        <Form.Item name="phone" label="Phone">
          <Input placeholder="020 0000 0000" />
        </Form.Item>
      </div>

      <Form.Item name="address_line1" label="Address Line 1">
        <Input placeholder="Street address…" />
      </Form.Item>

      <div className="grid grid-cols-2 gap-x-4">
        <Form.Item
          name="country_id"
          label="Country"
          rules={isEdit ? [] : [{ required: true, message: "Country is required" }]}
        >
          <Select
            showSearch
            placeholder="Search country…"
            filterOption={false}
            loading={loadingCountries}
            onSearch={(val) => setCountrySearch(val)}
            options={countries.map((country) => ({
              value: country.id,
              label: country.name,
            }))}
            onChange={(countryId) => {
              const country = countries.find((c) => c.id === countryId);
              setSelectedCountryId(countryId);
              setSelectedStateId(undefined);
              setStateSearch("");
              setCitySearch("");
              form.setFieldsValue({
                state_id: undefined,
                city_id: undefined,
                country: country?.name,
              });
            }}
          />
        </Form.Item>

        <Form.Item
          name="state_id"
          label="State / County"
          rules={isEdit ? [] : [{ required: true, message: "County is required" }]}
        >
          <Select
            showSearch
            placeholder={selectedCountryId ? "Search state…" : "Select country first"}
            filterOption={false}
            loading={loadingStates}
            disabled={!selectedCountryId}
            onSearch={(val) => setStateSearch(val)}
            options={states.map((state) => ({
              value: state.id,
              label: state.name,
            }))}
            onChange={(stateId) => {
              const state = states.find((s) => s.id === stateId);
              setSelectedStateId(stateId);
              setCitySearch("");
              form.setFieldsValue({
                city_id: undefined,
                county: state?.name,
              });
            }}
          />
        </Form.Item>
      </div>

      <div className="grid grid-cols-2 gap-x-4">
        <Form.Item
          name="city_id"
          label="City"
          rules={isEdit ? [] : [{ required: true, message: "City is required" }]}
        >
          <Select
            showSearch
            placeholder={selectedStateId ? "Search city…" : "Select state first"}
            filterOption={false}
            loading={loadingCities}
            disabled={!selectedStateId}
            onSearch={(val) => setCitySearch(val)}
            options={cities.map((city) => ({ value: city.id, label: city.name }))}
            onChange={(cityId) => {
              const city = cities.find((c) => c.id === cityId);
              form.setFieldsValue({ city: city?.name });
            }}
          />
        </Form.Item>

        <Form.Item name="postcode" label="Postcode">
          <Input placeholder="EC1A 1BB" />
        </Form.Item>
      </div>

      <div className="grid grid-cols-2 gap-x-4">
        <Form.Item
          name="payment_terms_days"
          label="Payment Terms (days)"
        >
          <InputNumber
            className="w-full"
            placeholder="30"
            min={0}
          />
        </Form.Item>

        <Form.Item
          name="credit_limit"
          label="Credit Limit (£)"
        >
          <InputNumber
            className="w-full"
            placeholder="10000"
            min={0}
            prefix="£"
          />
        </Form.Item>
      </div>

      <Form.Item name="vat_number" label="VAT Number">
        <Input placeholder="GB 123 4567 89" />
      </Form.Item>

      <Form.Item name="notes" label="Notes">
        <Input.TextArea
          rows={2}
          placeholder="Optional notes…"
        />
      </Form.Item>
    </>
  );
};

export default ClientForm;
